type TextureReferenceSide = {
    modelColor: MPlaza.Color;
    modelSide: MPlaza.Side;
};

type TextureReferenceFile = {
    filename: string;
    data: Uint8Array;
};

export type TextureReferenceDownloadProgress = {
    phase: 'generating' | 'packaging';
    completed: number;
    total: number;
    sideName?: string | null;
};

const REFERENCE_TEXTURE_SIZE = 512;
const ZIP_UTF8_FLAG = 0x0800;

const sanitizeFileNamePart = (value: string | null | undefined, fallback: string) => {
    const sanitized = (value ?? '')
        .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
        .replace(/\s+/g, ' ')
        .trim();

    return sanitized || fallback;
};

const makeUniqueFileName = (filename: string, usedFileNames: Set<string>) => {
    if (!usedFileNames.has(filename)) {
        usedFileNames.add(filename);
        return filename;
    }

    const extensionIndex = filename.lastIndexOf('.');
    const baseName = extensionIndex === -1 ? filename : filename.slice(0, extensionIndex);
    const extension = extensionIndex === -1 ? '' : filename.slice(extensionIndex);
    let index = 2;
    let candidate = `${baseName}_${index}${extension}`;

    while (usedFileNames.has(candidate)) {
        index++;
        candidate = `${baseName}_${index}${extension}`;
    }

    usedFileNames.add(candidate);
    return candidate;
};

const getTextureReferenceFileName = (modelSide: MPlaza.Side) => {
    const sideName = sanitizeFileNamePart(modelSide.get('name'), `side-${modelSide.get('sideID')}`);

    return `uv_reference_${sideName}.png`;
};

const getTextureReferencesZipFileName = (product: MPlaza.Model) => {
    return `uv_references_${sanitizeFileNamePart(product.get('name'), `product-${product.get('modelID')}`)}.zip`;
};

const getTextureReferenceSides = (product: MPlaza.Model) => {
    const sides: TextureReferenceSide[] = [];

    product.get('colors').each((modelColor) => {
        modelColor.get('sides').each((modelSide) => {
            sides.push({ modelColor, modelSide });
        });
    });

    return sides;
};

const getTextureReferenceSide = (product: MPlaza.Model, variantId: number, sideId: number) => {
    const modelColor = product.get('colors').get(variantId);

    if (!modelColor) throw new Error('Invalid variant');

    const modelSide = modelColor.get('sides').find((x) => x.get('sideID') === sideId);

    if (!modelSide) throw new Error('Invalid side');

    return { modelColor, modelSide };
};

const getVariantFolderNames = (sides: TextureReferenceSide[]) => {
    const usedFolderNames = new Set<string>();
    const folderNames = new Map<number, string>();

    for (const side of sides) {
        const colorId = side.modelColor.get('colorID');

        if (folderNames.has(colorId)) continue;

        folderNames.set(colorId, makeUniqueFileName(sanitizeFileNamePart(side.modelColor.get('name'), `variant-${colorId}`), usedFolderNames));
    }

    return folderNames;
};

const createDesignItemForItem = (item: paper.Item, area: MPlaza.Area) => {
    const designItem = new MPlaza.DesignItem();
    designItem.set('json', item.exportJSON({ asString: true }));
    designItem.set('svg', item.exportSVG({ asString: true, embedImages: false }) as string);
    designItem.updateAreas([area]);
    item.data.guid = designItem.get('itemGuid');

    return designItem;
};

const scaleTextToFit = (item: paper.PointText, width: number, divisor: number) => {
    if (item.bounds.width <= 0) return;

    const scale = width / (item.bounds.width * divisor);
    item.scaling.x = scale;
    item.scaling.y = scale;
};

export const createTextureReferenceDesignItems = (paperScope: paper.PaperScope, side: MPlaza.Side) => {
    const items: MPlaza.DesignItem[] = [];

    for (const area of side.get('areas').toArray()) {
        const areaItem = paperScope.project.importJSON(area.get('path')!);

        const x = area.get('boundsX');
        const y = area.get('boundsY');
        const width = area.get('boundsWidth');
        const height = area.get('boundsHeight');

        areaItem.strokeColor = new paperScope.Color('#555');
        areaItem.strokeWidth = 10;
        areaItem.fillColor = new paperScope.Color('rgba(255, 255, 255, 0.50)');
        areaItem.opacity = 0.5;
        areaItem.dashArray = [10, 10];

        areaItem.position.x = x + width / 2;
        areaItem.position.y = y + height / 2;

        const sideNameItem = new paperScope.PointText({ content: side.get('name') });
        sideNameItem.fillColor = new paperScope.Color('#555');
        sideNameItem.fontSize = 48;
        sideNameItem.fontFamily = 'Inter,sans-serif';
        scaleTextToFit(sideNameItem, width, 2);
        sideNameItem.position.x = x + width / 2;
        sideNameItem.position.y = y + height / 2;

        const clientDesignItem = new paperScope.PointText({ content: "Client's design" });
        clientDesignItem.fillColor = new paperScope.Color('#555');
        clientDesignItem.fontSize = 24;
        clientDesignItem.fontFamily = 'Inter,sans-serif';
        clientDesignItem.style.fontWeight = 'bold';
        scaleTextToFit(clientDesignItem, width, 2);
        clientDesignItem.position.x = x + width / 2;
        clientDesignItem.position.y = y + height / 2 + (sideNameItem.bounds.height / 2) * 1.2;

        items.push(createDesignItemForItem(areaItem, area));
        items.push(createDesignItemForItem(sideNameItem, area));
        items.push(createDesignItemForItem(clientDesignItem, area));
    }

    return items;
};

const createTextureReferenceDesign = (product: MPlaza.Model, side: MPlaza.Side) => {
    const design = new MPlaza.Design();
    design.setModel(product);

    const paperScope = new paper.PaperScope();
    paperScope.setup(new paperScope.Size(10, 10));
    paperScope.activate();

    for (const designItem of createTextureReferenceDesignItems(paperScope, side)) {
        design.get('designItems').push(designItem);
    }

    return design;
};

const createTextureReferenceDataUrl = async (product: MPlaza.Model, modelSide: MPlaza.Side) => {
    const referenceDesign = createTextureReferenceDesign(product, modelSide);
    const canvas = document.createElement('canvas');
    canvas.width = REFERENCE_TEXTURE_SIZE;
    canvas.height = REFERENCE_TEXTURE_SIZE;

    try {
        const customizer = new MPlaza.Customizer(true, false, canvas, false);
        customizer.showSideImage = false;
        customizer.fitToScreenMargin = 0;
        customizer.setDesign(referenceDesign);

        await customizer.setSide(modelSide);
        customizer.adaptZoomToSideAreas(false);
        await new Promise<void>((resolve) => setTimeout(resolve, 1000));

        return customizer.toDataURL();
    } finally {
        canvas.width = 1;
        canvas.height = 1;
    }
};

const dataUrlToBytes = (dataUrl: string) => {
    const commaIndex = dataUrl.indexOf(',');

    if (commaIndex === -1) throw new Error('Invalid data URL');

    const metadata = dataUrl.slice(0, commaIndex);
    const data = dataUrl.slice(commaIndex + 1);

    if (metadata.includes(';base64')) {
        const binary = atob(data);
        const bytes = new Uint8Array(binary.length);

        for (let index = 0; index < binary.length; index++) {
            bytes[index] = binary.charCodeAt(index);
        }

        return bytes;
    }

    return new TextEncoder().encode(decodeURIComponent(data));
};

const triggerDownload = (href: string, filename: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const downloadBlob = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    triggerDownload(url, filename);
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

const crc32Table = (() => {
    const table = new Uint32Array(256);

    for (let index = 0; index < table.length; index++) {
        let value = index;

        for (let bit = 0; bit < 8; bit++) {
            value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
        }

        table[index] = value >>> 0;
    }

    return table;
})();

const getCrc32 = (data: Uint8Array) => {
    let crc = 0xffffffff;

    for (let index = 0; index < data.length; index++) {
        crc = crc32Table[(crc ^ data[index]) & 0xff] ^ (crc >>> 8);
    }

    return (crc ^ 0xffffffff) >>> 0;
};

const getDosDateTime = (date: Date) => {
    const year = Math.min(Math.max(date.getFullYear(), 1980), 2107);
    const dosTime = (date.getHours() << 11) | (date.getMinutes() << 5) | Math.floor(date.getSeconds() / 2);
    const dosDate = ((year - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate();

    return { dosDate, dosTime };
};

const writeUint16 = (view: DataView, offset: number, value: number) => {
    view.setUint16(offset, value, true);
    return offset + 2;
};

const writeUint32 = (view: DataView, offset: number, value: number) => {
    view.setUint32(offset, value, true);
    return offset + 4;
};

const toBlobPart = (bytes: Uint8Array): ArrayBuffer => {
    const copy = new Uint8Array(bytes.byteLength);
    copy.set(bytes);
    return copy.buffer;
};

export const downloadTextureReference = async (product: MPlaza.Model, variantId: number, sideId: number) => {
    const { modelSide } = getTextureReferenceSide(product, variantId, sideId);

    const dataUrl = await createTextureReferenceDataUrl(product, modelSide);
    const blob = new Blob([toBlobPart(dataUrlToBytes(dataUrl))], { type: 'image/png' });
    downloadBlob(blob, getTextureReferenceFileName(modelSide));
};

const createZipBlob = (files: TextureReferenceFile[]) => {
    const textEncoder = new TextEncoder();
    const { dosDate, dosTime } = getDosDateTime(new Date());
    const localParts: BlobPart[] = [];
    const centralParts: BlobPart[] = [];
    const entries: {
        filenameBytes: Uint8Array;
        data: Uint8Array;
        crc32: number;
        localHeaderOffset: number;
    }[] = [];
    let offset = 0;

    for (const file of files) {
        const filenameBytes = textEncoder.encode(file.filename);
        const crc32 = getCrc32(file.data);
        const localHeader = new Uint8Array(30);
        const view = new DataView(localHeader.buffer);
        let headerOffset = 0;

        headerOffset = writeUint32(view, headerOffset, 0x04034b50);
        headerOffset = writeUint16(view, headerOffset, 20);
        headerOffset = writeUint16(view, headerOffset, ZIP_UTF8_FLAG);
        headerOffset = writeUint16(view, headerOffset, 0);
        headerOffset = writeUint16(view, headerOffset, dosTime);
        headerOffset = writeUint16(view, headerOffset, dosDate);
        headerOffset = writeUint32(view, headerOffset, crc32);
        headerOffset = writeUint32(view, headerOffset, file.data.length);
        headerOffset = writeUint32(view, headerOffset, file.data.length);
        headerOffset = writeUint16(view, headerOffset, filenameBytes.length);
        writeUint16(view, headerOffset, 0);

        localParts.push(toBlobPart(localHeader), toBlobPart(filenameBytes), toBlobPart(file.data));
        entries.push({ filenameBytes, data: file.data, crc32, localHeaderOffset: offset });
        offset += localHeader.length + filenameBytes.length + file.data.length;
    }

    const centralDirectoryOffset = offset;

    for (const entry of entries) {
        const centralHeader = new Uint8Array(46);
        const view = new DataView(centralHeader.buffer);
        let headerOffset = 0;

        headerOffset = writeUint32(view, headerOffset, 0x02014b50);
        headerOffset = writeUint16(view, headerOffset, 20);
        headerOffset = writeUint16(view, headerOffset, 20);
        headerOffset = writeUint16(view, headerOffset, ZIP_UTF8_FLAG);
        headerOffset = writeUint16(view, headerOffset, 0);
        headerOffset = writeUint16(view, headerOffset, dosTime);
        headerOffset = writeUint16(view, headerOffset, dosDate);
        headerOffset = writeUint32(view, headerOffset, entry.crc32);
        headerOffset = writeUint32(view, headerOffset, entry.data.length);
        headerOffset = writeUint32(view, headerOffset, entry.data.length);
        headerOffset = writeUint16(view, headerOffset, entry.filenameBytes.length);
        headerOffset = writeUint16(view, headerOffset, 0);
        headerOffset = writeUint16(view, headerOffset, 0);
        headerOffset = writeUint16(view, headerOffset, 0);
        headerOffset = writeUint16(view, headerOffset, 0);
        headerOffset = writeUint32(view, headerOffset, 0);
        writeUint32(view, headerOffset, entry.localHeaderOffset);

        centralParts.push(toBlobPart(centralHeader), toBlobPart(entry.filenameBytes));
        offset += centralHeader.length + entry.filenameBytes.length;
    }

    const centralDirectorySize = offset - centralDirectoryOffset;
    const endRecord = new Uint8Array(22);
    const endRecordView = new DataView(endRecord.buffer);
    let endRecordOffset = 0;

    endRecordOffset = writeUint32(endRecordView, endRecordOffset, 0x06054b50);
    endRecordOffset = writeUint16(endRecordView, endRecordOffset, 0);
    endRecordOffset = writeUint16(endRecordView, endRecordOffset, 0);
    endRecordOffset = writeUint16(endRecordView, endRecordOffset, entries.length);
    endRecordOffset = writeUint16(endRecordView, endRecordOffset, entries.length);
    endRecordOffset = writeUint32(endRecordView, endRecordOffset, centralDirectorySize);
    endRecordOffset = writeUint32(endRecordView, endRecordOffset, centralDirectoryOffset);
    writeUint16(endRecordView, endRecordOffset, 0);

    return new Blob([...localParts, ...centralParts, toBlobPart(endRecord)], { type: 'application/zip' });
};

export const downloadProductTextureReferences = async (product: MPlaza.Model, onProgress?: (progress: TextureReferenceDownloadProgress) => void) => {
    const sides = getTextureReferenceSides(product);

    if (sides.length === 0) throw new Error('No product sides found');

    const hasMultipleVariants = product.get('colors').length > 1;
    const variantFolderNames = hasMultipleVariants ? getVariantFolderNames(sides) : null;
    const usedFileNames = new Set<string>();
    const files: TextureReferenceFile[] = [];

    for (let index = 0; index < sides.length; index++) {
        const side = sides[index];
        onProgress?.({
            phase: 'generating',
            completed: index,
            total: sides.length,
            sideName: side.modelSide.get('name')
        });

        const dataUrl = await createTextureReferenceDataUrl(product, side.modelSide);
        const fileName = getTextureReferenceFileName(side.modelSide);
        const variantFolderName = variantFolderNames?.get(side.modelColor.get('colorID'));

        files.push({
            filename: makeUniqueFileName(variantFolderName ? `${variantFolderName}/${fileName}` : fileName, usedFileNames),
            data: dataUrlToBytes(dataUrl)
        });

        onProgress?.({
            phase: 'generating',
            completed: index + 1,
            total: sides.length,
            sideName: side.modelSide.get('name')
        });
    }

    onProgress?.({
        phase: 'packaging',
        completed: sides.length,
        total: sides.length
    });

    if (files.length === 1) {
        const [file] = files;
        const blob = new Blob([toBlobPart(file.data)], { type: 'image/png' });
        downloadBlob(blob, file.filename);
        return;
    }

    downloadBlob(createZipBlob(files), getTextureReferencesZipFileName(product));
};
